import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { transportProviderAPI } from '../../services/api';

// Mirrors pages/CompanyRegisterPage.jsx closely — same public
// register-then-wait-for-approval shape, one org type over.
const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input {...props} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </div>
);

const FileField = ({ label, onChange, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" required={required} onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm file:ml-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 file:text-xs file:font-semibold" />
  </div>
);

const PROVIDER_TYPES = [
  { key: 'individual_driver', label: 'سائق فردي' },
  { key: 'vehicle_owner', label: 'مالك مركبة' },
  { key: 'transport_office', label: 'مكتب نقل' },
  { key: 'transport_company', label: 'شركة نقل' },
  { key: 'logistics_company', label: 'شركة لوجستية' },
  { key: 'fleet_owner', label: 'مالك أسطول' },
];

// Same set as the backend's REQUIRES_CR (transportAdminController.js) —
// duplicated here only to decide whether to *show* the CR field at all;
// the backend is still the actual enforcement point.
const REQUIRES_CR = new Set(['transport_office', 'transport_company', 'logistics_company', 'fleet_owner']);

const TransportProviderRegisterPage = () => {
  const [form, setForm] = useState({
    providerName: '', providerType: 'individual_driver', city: '', address: '', providerPhone: '', providerEmail: '',
    ownerName: '', ownerPhone: '', ownerEmail: '', ownerPassword: '', ownerPasswordConfirm: '',
    servicesCargo: true, servicesRides: false,
  });
  const [idDocument, setIdDocument] = useState(null);
  const [crDocument, setCrDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const needsCr = REQUIRES_CR.has(form.providerType);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.ownerPassword !== form.ownerPasswordConfirm) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }
    if (form.ownerPassword.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (!form.servicesCargo && !form.servicesRides) {
      setError('يرجى اختيار نوع خدمة واحد على الأقل: نقل بضائع أو مشاوير');
      return;
    }
    if (!idDocument) {
      setError('يرجى رفع صورة الهوية');
      return;
    }
    if (needsCr && !crDocument) {
      setError('يرجى رفع صورة السجل التجاري');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('idDocument', idDocument);
      if (crDocument) fd.append('crDocument', crDocument);
      await transportProviderAPI.register(fd);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ. يرجى المحاولة مجدداً.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center" dir="rtl">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">تم تقديم الطلب بنجاح!</h2>
          <p className="text-gray-500 text-sm mb-6">
            سيتم مراجعة طلبك من قبل إدارة المنصة. ستتمكن من تسجيل الدخول فور قبول الطلب خلال 24-48 ساعة.
          </p>
          <Link to="/transport-provider/login" className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800">
            العودة لتسجيل الدخول
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-600 p-4" dir="rtl">
      <div className="max-w-2xl mx-auto">
        <div className="text-center text-white py-6 sm:py-8">
          <div className="text-4xl sm:text-5xl mb-2">🚚</div>
          <h1 className="text-2xl sm:text-3xl font-bold">سافر — انضم كمزود خدمة نقل</h1>
          <p className="text-blue-200 mt-2 text-sm sm:text-base">نقل بضائع أو مشاوير — ابدأ باستقبال الطلبات وتقديم عروضك</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 mb-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-8">
            <div>
              <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-2">
                🚛 بيانات مزود الخدمة
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="اسم مزود الخدمة *" type="text" placeholder="مثال: محمد للنقل والشحن" required value={form.providerName} onChange={set('providerName')} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع مزود الخدمة</label>
                  <select className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.providerType} onChange={set('providerType')}>
                    {PROVIDER_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <Field label="المدينة" type="text" placeholder="الخرطوم" value={form.city} onChange={set('city')} />
                <Field label="العنوان الكامل *" type="text" placeholder="الحي، الشارع، أقرب علامة مميزة" required value={form.address} onChange={set('address')} />
                <Field label="رقم هاتف مزود الخدمة *" type="tel" placeholder="0900000000" required value={form.providerPhone} onChange={set('providerPhone')} />
                <Field label="البريد الإلكتروني (اختياري)" type="email" placeholder="info@example.com" value={form.providerEmail} onChange={set('providerEmail')} />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">نوع الخدمة * (اختر واحداً أو كلاهما)</label>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-lg px-4 py-2.5 flex-1 cursor-pointer">
                    <input type="checkbox" checked={form.servicesCargo} onChange={(e) => setForm((f) => ({ ...f, servicesCargo: e.target.checked }))} />
                    📦 نقل بضائع
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-lg px-4 py-2.5 flex-1 cursor-pointer">
                    <input type="checkbox" checked={form.servicesRides} onChange={(e) => setForm((f) => ({ ...f, servicesRides: e.target.checked }))} />
                    🚕 مشاوير
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-2">
                📄 وثائق التحقق
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FileField label="صورة الهوية الشخصية *" required onChange={(e) => setIdDocument(e.target.files[0])} />
                {needsCr && (
                  <FileField label="صورة السجل التجاري *" required onChange={(e) => setCrDocument(e.target.files[0])} />
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2">JPG، PNG أو PDF — بحد أقصى 5 ميجابايت</p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4 flex items-center gap-2">
                👤 بياناتك الشخصية (حساب المالك)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="الاسم الكامل *" type="text" placeholder="محمد أحمد" required value={form.ownerName} onChange={set('ownerName')} />
                <Field label="رقم الهاتف *" type="tel" placeholder="0900000001" required value={form.ownerPhone} onChange={set('ownerPhone')} />
                <Field label="البريد الإلكتروني *" type="email" placeholder="you@example.com" required value={form.ownerEmail} onChange={set('ownerEmail')} />
                <div />
                <Field label="كلمة المرور *" type="password" placeholder="6 أحرف على الأقل" required value={form.ownerPassword} onChange={set('ownerPassword')} />
                <Field label="تأكيد كلمة المرور *" type="password" placeholder="أعد إدخال كلمة المرور" required value={form.ownerPasswordConfirm} onChange={set('ownerPasswordConfirm')} />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              ⚠️ سيتم مراجعة طلبك من قبل إدارة المنصة قبل تفعيل حسابك. قد تستغرق العملية حتى 48 ساعة.
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-bold py-3 rounded-lg text-lg transition">
              {loading ? 'جاري إرسال الطلب...' : 'تقديم طلب التسجيل'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            لديك حساب بالفعل؟{' '}
            <Link to="/transport-provider/login" className="text-blue-600 hover:underline font-medium">تسجيل الدخول</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransportProviderRegisterPage;
